import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Typography,
  Popover,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Button
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import VisibilityIcon from '@mui/icons-material/Visibility';
import {  vendorApi } from "../../api/api";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import EditApartmentModal from "../apartments/EditApartmentModal";

const Apartment = () => {
  const [rows, setRows] = React.useState([]);
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = React.useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [pageSize, setPageSize] = useState(25);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editApartment, setEditApartment] = useState(null);



  const handleActionMenuOpen = (event, row) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedRow(null);
  };

  const handleIconClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };



  const handleAction = (action, row) => {
    if (!row || !row.ApartmentName) {
      return;
    }



    if (action === "edit") {
      setEditApartment(row);
      setEditOpen(true);
    }
    else if (action === "view") {
      if (Number(row.PropertyType) === 3) {
        navigate(`/apartment/viewApartment?propertyId=${row.id}`, {
          state: {
            PropertyId: row.id,
            ApartmentName: row.ApartmentName,
            PropertyName: row.PropertyName,
            City: row.City,
            PropertyType: row.PropertyType,
            Address: row.Address,
            SizeInSqFt: row.SizeInSqFt,
            percentagevalue: row.percentagevalue,
            sqftamount: row.sqftamount,
            UnitPrice: row.UnitPrice,
            IconUrl: row.IconUrl,
          },
        });
      }
    }

    const routeMap = {
      approve: "/property/approveProperty",
    };

    if (routeMap[action]) {
      navigate(`${routeMap[action]}?propertyId=${row.id}`, {
        state: { ...row },
      });
    }
  };

  const handleFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  useEffect(() => {
    const getProperties = async () => {
      try {
        const response = await vendorApi.getAllApartments();
        const data = response.data.data || [];
      
        const formattedData = data.map((item) => ({
          id: item.id,
          VendorName: item.UserName,
          ApartmentName: item.ApartmentName,
          City: item.City,
          Status: item.Status,

          PropertyType: item.PropertyType,

          Address: item.Address,


          NoOfPlots: item.NoOfPlots,
          IconUrl: item.IconUrl,

        }));
        setRows(formattedData);
      } catch (error) {
        
        setRows([]); // Optional: reset table
      }
    };

    getProperties();
  }, []);

  const filteredData = rows.filter((row) => {
    const matchesStatus =
      statusFilter === "All" || row.Status === statusFilter;

    const matchesSearch =
      searchQuery === "" ||
      Object.values(row)
        .join(" ")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    return (
      matchesSearch &&
      matchesStatus 
    );
  });

  const renderCellWithFallback = (params) => {
    return params.value === null || params.value === undefined || params.value === ''
      ? '------'
      : params.value;
  };

  const columns = [
    { field: "id", headerName: "ID", width: 50, renderCell: renderCellWithFallback },
    { field: "ApartmentName", headerName: "Apartment Name", width: 200, renderCell: renderCellWithFallback },
    { field: "City", headerName: "City", width: 200, renderCell: renderCellWithFallback },
    { field: "Address", headerName: "Address", width: 250, renderCell: renderCellWithFallback },
    {
      field: "Status",
      headerName: "Status",
      width: 200,
      sortComparator: (a, b) => {
        const order = {
          Pending: 1,
          InProgress: 2,
          Approved: 3,
          Sold: 4,
          Rejected: 5,
          Other: 6,
        };

        const getOrder = (value) => order[value] || order.Other;

        return getOrder(a) - getOrder(b);
      },
      renderCell: (params) => {
        const status = params.value;

        let color = "gray";
        switch (status) {
          case "Approved":
            color = "green";
            break;
          case "Sold":
            color = "red";
            break;
          case "InProgress":
            color = "#FFA500";
            break;
          case "Pending":
            color = "#1976d2";
            break;
          case "Rejected":
            color = "orange";
            break;
          case "sale Initiated":
            color = 'blue';
            break;
          default:
            color = "gray";
        }

        return (
          <span
            style={{
              fontWeight: "bold",
              color,
              padding: "4px 8px",
              borderRadius: "8px",
              backgroundColor: `${color}20`,
              display: "inline-block",
              minWidth: "90px",
              textAlign: "center",
            }}
          >
            {status || "------"}
          </span>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Button
            variant="outlined"
            size="small"
            endIcon={<ArrowDropDownIcon />}
            onClick={(event) => handleActionMenuOpen(event, params.row)}
            sx={{
              textTransform: 'none',
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.light',
              }
            }}
          >
            Actions
          </Button>
        );
      },
    },
    { field: "VendorName", headerName: "Vendor Name", width: 200, renderCell: renderCellWithFallback },
  ];

  const [selectedFields, setSelectedFields] = useState(
    columns.map((col) => col.field)
  );
  const displayedColumns = columns.filter((col) =>
    selectedFields.includes(col.field)
  );

  const open = Boolean(anchorEl);
  const id = open ? "column-popover" : undefined;

  return (

    <>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1, mt: 1 }}>
          Apartment
        </Typography>

      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 3,
          overflowX: "hidden",
          width: "100%",
        }}
      >
        <div style={{ width: '100%' }}>
          <Card
            sx={{
              flexGrow: 1,
              border: "1px solid lightgray",
              borderRadius: "5px",
            }}
          >
            <CardContent>
              <Grid
                container
                spacing={2}
                alignItems="center"
                direction="row"
                sx={{
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                {/* Search Field */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Search By Key words"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                    }}
                  />
                </Grid>

                {/* Status Dropdown */}
                <Grid item xs={12} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={handleFilterChange}
                      label="Status"
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Approved">Approved</MenuItem>
                      <MenuItem value="Sold">Sold</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="InProgress">InProgress</MenuItem>
                      <MenuItem value="Rejected">Rejected</MenuItem>
                      <MenuItem value="sale Initiated">Sale Initiated</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Icon Button (Right end) */}
                <Grid item xs={12} sm="auto" md="auto">
                  <IconButton onClick={handleIconClick}>
                    <ViewColumnIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card style={{ height: "100vh", overflowX: "hidden", marginTop: "10px", backgroundColor: "#f4f1f1ff" }}>
            <DataGrid
              rows={filteredData}
              columns={displayedColumns}
              pageSize={pageSize}
              disableSelectionOnClick
              rowsPerPageOptions={[25, 50, 100]}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              sx={{
                height: 650,
                border: '1px solid #ccc',
                borderRadius: 2,
                fontSize: '0.9rem',
                backgroundColor: '#ffffff',
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f0f0f0ff',
                },
                '& .MuiDataGrid-cell': {
                  color: '#333',
                  borderBottom: '1px solid #ddd',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f0f0f0ff',
                  color: 'black',
                  fontWeight: 'bold',
                },
                '& .MuiTablePagination-root': {
                  fontSize: '0.875rem',
                },
              }}
            />
          </Card>
        </div>

        {/* Actions Dropdown Menu */}
        <Popover
          open={Boolean(actionMenuAnchor)}
          anchorEl={actionMenuAnchor}
          onClose={handleActionMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 1, minWidth: 200 }}>
            {selectedRow && (
              <>
                {/* View Action */}
                <MenuItem
                  onClick={() => handleAction("view", selectedRow)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <VisibilityIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  View
                </MenuItem>

                <MenuItem
                  onClick={() => handleAction("edit", selectedRow)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <VisibilityIcon fontSize="small" sx={{ color: 'primary.main' }} />
                 Edit
                </MenuItem>
               
              </>
            )}
          </Box>
        </Popover>

        {/* Column Selection Popover */}
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Box sx={{ p: 2, maxWidth: 300 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select Columns
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {columns.map((col) => (
                <FormControl
                  key={col.field}
                  component="fieldset"
                  sx={{ m: 0, p: 0 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedFields.includes(col.field)}
                        onChange={() => {
                          setSelectedFields((prev) =>
                            prev.includes(col.field)
                              ? prev.filter((f) => f !== col.field)
                              : [...prev, col.field]
                          );
                        }}
                        sx={{
                          '&.Mui-checked': {
                            color: 'skyblue',
                          },
                        }}
                      />
                    }
                    label={col.headerName}
                    sx={{ m: 0 }}
                  />
                </FormControl>
              ))}
            </Box>
          </Box>
        </Popover>

       
      </Box>

      <EditApartmentModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        apartmentId={editApartment?.id}
        onUpdated={() => {
          vendorApi.getAllApartments().then(res => {
            setRows(res.data.data || []);
          });
        }}
      />
    </>

  );
};

export default Apartment;